<script setup lang="ts">
/**
 * Approve a withdraw request — operator picks the treasury bank
 * account that will fund the outbound transfer.
 *
 * The picker is populated from `useTreasuryBankAccountsQuery({
 * status: 'active', currencyCode: <request.currencyCode> })`. The
 * row marked `isDefault: true` is auto-selected when the dialog
 * opens. The selected account's `accountName + bankName` is shown in
 * the confirm copy so the operator sees the consequence before
 * clicking Approve.
 */
import { computed, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Select from '@uiKit/elements/Select.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'
import type { TreasuryBankAccount } from '../../treasuryAccounts'

interface Props {
  visible: boolean
  currencyCode: string
  accounts: TreasuryBankAccount[]
  accountsLoading?: boolean
  loading?: boolean
  apiError?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  accountsLoading: false,
  loading: false,
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', value: { treasuryBankAccountId: string }): void
}>()

const accounts = computed(() => props.accounts ?? [])
const accountsLoading = computed(() => props.accountsLoading)

const selectedId = ref<string | null>(null)
const localError = ref('')

const options = computed(() =>
  (accounts.value ?? []).map((account: TreasuryBankAccount) => ({
    label: `${account.accountName} · ${account.bankName}`,
    value: account.id,
  })),
)

const selectedAccount = computed(
  () => (accounts.value ?? []).find((account) => account.id === selectedId.value) ?? null,
)

// Auto-select the default account when the dialog opens or when the
// accounts list resolves for the first time.
watch(
  [() => props.visible, accounts],
  ([isVisible, list]) => {
    if (!isVisible) return
    if (selectedId.value && list?.some((a) => a.id === selectedId.value)) return
    const def = list?.find((a) => a.isDefault) ?? list?.[0] ?? null
    selectedId.value = def?.id ?? null
  },
  { immediate: true },
)

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      localError.value = ''
    }
  },
)

function handleConfirm() {
  if (!selectedId.value) {
    localError.value = 'Pick a treasury bank account to fund this transfer.'
    return
  }
  localError.value = ''
  emit('confirm', { treasuryBankAccountId: selectedId.value })
}

function handleClose() {
  if (props.loading) return
  emit('update:visible', false)
}
</script>

<template>
  <Dialog
    :visible="visible"
    width="520px"
    max-height="80vh"
    :show-close-icon="!loading"
    :dismissable="!loading"
    @update:visible="(v) => !v && handleClose()"
  >
    <template #header>
      <Text type="page-title">Approve withdraw request</Text>
    </template>

    <Box flex direction="col" gap="3">
      <Text type="body-sm" color="muted">
        Pick the treasury account that will fund the outbound transfer. The investor's destination
        bank account is debited from the selected treasury account in the next settlement run.
      </Text>

      <FormField label="Treasury bank account" required :error="localError">
        <Select
          v-model="selectedId"
          :options="options"
          :loading="accountsLoading"
          :disabled="loading || accountsLoading"
          placeholder="Choose a treasury account"
          filter
        />
      </FormField>

      <Box v-if="selectedAccount" class="confirm-summary" padding="3">
        <Text type="body-sm">
          Funding from
          <Text is="span" type="body-sm" weight="semibold">{{ selectedAccount.accountName }}</Text>
          at
          <Text is="span" type="body-sm" weight="semibold">{{ selectedAccount.bankName }}</Text> ({{
            selectedAccount.currencyCode
          }}).
        </Text>
      </Box>

      <Message v-if="apiError" color="danger" variant="outlined" size="small" :message="apiError" />
    </Box>

    <template #footer>
      <Box flex justify="end" gap="2">
        <Button
          label="Cancel"
          variant="text"
          color="secondary"
          :disabled="loading"
          @click="handleClose"
        />
        <Button label="Approve" color="primary" :loading="loading" @click="handleConfirm" />
      </Box>
    </template>
  </Dialog>
</template>

<style scoped>
.confirm-summary {
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-200, var(--p-primary-100));
  border-radius: var(--p-border-radius-md);
}
</style>
